import { useMemo, useState } from 'react';
import i18n, { t } from 'i18next';

import Info from '@/assets/icons/info.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import FishHealthFilter from '@/components/molecules/FishHealthFilter';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { FullSizeImage } from '@/domain/images';
import { FILTER_TAGS, fullSizeWoundImageMapper } from '@/domain/images';
import { useImageState } from '@/hooks/image-state';
import { localizedDateFormat } from '@/utils/date';

import styles from './CageWelfareWoundDetections.module.scss';

const WOUND_FILTER_TAGS = FILTER_TAGS.filter((filter) => filter.tag !== 'maturation_tag');

export interface CageWelfareWoundDetectionsPageContentProps {
    isOptoScaleAdmin?: boolean;
    /** Null while the images load. */
    imagesData: FullSizeImage[] | null | undefined;
    cageId: number;
    /** The pen's location zone; useImageState reads UTC until it is known. */
    timezone: string | undefined;
    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
}

export default function CageWelfareWoundDetectionsPageContent(
    props: CageWelfareWoundDetectionsPageContentProps
) {
    const {
        isOptoScaleAdmin = false,
        onCommitFalseDetection = () => {},
        cageId,
        timezone,
        imagesData,
    } = props;
    const mapper = useMemo(() => fullSizeWoundImageMapper(cageId), [cageId]);
    const [selectedImageFilterTag, setSelectedImagedFilterTag] = useState(WOUND_FILTER_TAGS[0].tag);
    const selectedFilter = useMemo(
        () => WOUND_FILTER_TAGS.find((tag) => tag.tag === selectedImageFilterTag)?.filter,
        [selectedImageFilterTag]
    );

    const imageState = useImageState({
        loading: false,
        mapper,
        data: imagesData,
        queryParam: 'imageId',
        timezone,
        filter: selectedFilter,
    });

    const last = imageState.dayswithimages[imageState.dayswithimages.length - 1];
    const onFilterGroupChangeHandler = (tag: string) => {
        setSelectedImagedFilterTag(tag);
    };

    const imageViewerKey = useMemo(() => {
        return `woundDetections-${selectedImageFilterTag}-${imageState?.initialImageIndex}`;
    }, [selectedImageFilterTag, imageState?.initialImageIndex]);

    return (
        <div className={styles.pageBoxLayout}>
            <ContentBlock size="compact" spacing="none">
                {imageState.loading && <Spinner size="lg" />}
                {!imageState.loading && (
                    <div>
                        <ImageViewer
                            key={imageViewerKey}
                            variant="woundDetections"
                            isOptoScaleAdmin={isOptoScaleAdmin}
                            onCommitImageFeedback={onCommitFalseDetection}
                            options={{ boxes: 'on', showHelp: false }}
                            images={imageState.images}
                            daysWithImages={imageState.dayswithimages}
                            onDayChange={imageState.onAfterTimelineChangeHandler}
                            initialImageIndex={imageState.initialImageIndex}
                            isFetching={imageState.isFetching}
                        />

                        <div>
                            <FishHealthFilter
                                filterStateList={WOUND_FILTER_TAGS}
                                onFilterGroupChange={onFilterGroupChangeHandler}
                                currentFilterStateList={selectedImageFilterTag}
                            />
                        </div>
                    </div>
                )}
            </ContentBlock>
            <ContentBlock
                title={t('common.labels.information') as string}
                titleIcon={<TitleIcon icon={Info} />}>
                <p>{t('cageWelfare.wounds.showingImagesWhereWoundsHas')}</p>
                {last && (
                    <MetricSummaryRow
                        flush
                        label={t('common.labels.lastMeasurement') as string}
                        value={localizedDateFormat({
                            dateString: last.day,
                            onlydate: true,
                            locale: i18n.language,
                        })}
                    />
                )}
            </ContentBlock>
        </div>
    );
}
